import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, X, Loader2, Sparkles, AlertTriangle, CheckCircle, PiggyBank, Leaf } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { FinancialAdvisor, formatAIResponse } from '../utils/financialIntelligence';
import { personalities, getPersonalityResponse, Personality } from '../utils/personalityTypes';
import { UserProfileManager, UserProfile, FinancialHabit } from '../utils/userProfile';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  category?: 'financial' | 'sustainability' | 'alert' | 'success';
  timestamp?: Date;
  tips?: string[];
}

interface AIAssistantContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  messages: Message[];
  addMessage: (message: Message) => void;
  personality: Personality;
  setPersonality: (personality: Personality) => void;
  userProfile: UserProfile | null;
  updateUserProfile: (profile: Partial<UserProfile>) => void;
}

export const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }
  return context;
};

export const AIAssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [personality, setPersonality] = useState<Personality>(() => {
    const savedProfile = UserProfileManager.getProfile();
    if (savedProfile?.preferences.preferredPersonality) {
      return personalities.find(p => p.id === savedProfile.preferences.preferredPersonality) || personalities[0];
    }
    return personalities[0];
  });

  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    return UserProfileManager.getProfile();
  });

  const updateUserProfile = (updates: Partial<UserProfile>) => {
    setUserProfile(prev => {
      const updated = prev ? { ...prev, ...updates } : null;
      if (updated) {
        UserProfileManager.saveProfile(updated);
      }
      return updated;
    });
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: personalities[0].messageStyle.greeting,
      category: 'success'
    }
  ]);

  const addMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  return (
    <AIAssistantContext.Provider value={{ 
      isOpen, 
      setIsOpen, 
      messages, 
      addMessage, 
      personality, 
      setPersonality,
      userProfile,
      updateUserProfile
    }}>
      {children}
      <AIAssistant />
    </AIAssistantContext.Provider>
  );
};

interface UserContext {
  income?: number;
  expenses?: number;
  goals?: string[];
  riskTolerance?: 'low' | 'medium' | 'high';
}

const PersonalitySelector: React.FC<{
  onSelect: (personality: Personality) => void;
  currentPersonality: Personality;
}> = ({ onSelect, currentPersonality }) => {
  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Choose Pocket's Personality:
      </h4>
      <div className="grid grid-cols-2 gap-2">
        {personalities.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            className={`
              p-2 rounded-lg text-left transition-all
              ${p.id === currentPersonality.id
                ? 'bg-[#68A511] text-white'
                : 'bg-gray-100 dark:bg-gray-700 hover:bg-[#68A511]/20'
              }
            `}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">{p.emoji}</span>
              <span className="text-sm font-medium">{p.name}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const AIAssistant = () => {
  const { 
    isOpen, 
    setIsOpen, 
    messages, 
    addMessage, 
    personality, 
    setPersonality,
    userProfile,
    updateUserProfile
  } = useAIAssistant();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const [showPersonalitySelector, setShowPersonalitySelector] = useState(false);

  // Reset typing state when route changes
  useEffect(() => {
    setIsTyping(false);
  }, [location]);

  const scrollToBottom = (smooth: boolean = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate contextual help based on current route
  const getContextualHelp = (): Message => {
    const path = location.pathname;
    const baseResponse = {
      id: Date.now().toString(),
      type: 'assistant' as const,
    };

    switch (true) {
      case path.includes('/dashboard/overview'):
        return {
          ...baseResponse,
          content: 'I see you\'re on the Overview page. Would you like me to explain your financial summary or help you understand any specific metrics?',
          category: 'financial'
        };
      case path.includes('/dashboard/transactions'):
        return {
          ...baseResponse,
          content: 'Looking at your transactions? I can help you categorize spending or identify areas for potential savings.',
          category: 'financial'
        };
      case path.includes('/dashboard/sustainability'):
        return {
          ...baseResponse,
          content: 'On the Sustainability page, I can help you understand your environmental impact and suggest eco-friendly financial choices.',
          category: 'sustainability'
        };
      default:
        return {
          ...baseResponse,
          content: 'How can I assist you with your financial and sustainability goals today?',
          category: 'success'
        };
    }
  };

  // Add announcement for screen readers
  const announceMessage = (message: Message) => {
    const announcement = `${message.type === 'user' ? 'You' : 'Assistant'}: ${message.content}`;
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(announcement);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Track user habits
  const trackHabit = (input: string) => {
    const spendingMatch = input.match(/spend|bought|purchase|paid/i);
    const savingMatch = input.match(/save|saved|saving/i);
    const amountMatch = input.match(/\$?\d+(?:,\d{3})*(?:\.\d{2})?/);

    if ((spendingMatch || savingMatch) && amountMatch) {
      const amount = parseFloat(amountMatch[0].replace(/[$,]/g, ''));
      const habit: FinancialHabit = {
        id: Date.now().toString(),
        category: spendingMatch ? 'spending' : 'saving',
        behavior: input,
        frequency: 1,
        impact: spendingMatch ? 'negative' : 'positive',
        amount,
        timestamp: new Date()
      };

      updateUserProfile({
        habits: [...(userProfile?.habits || []), habit]
      });
    }
  };

  // Enhanced message generation with personality and habits
  const generateResponse = (userInput: string): Message => {
    // Track habit from user input
    trackHabit(userInput);

    const response = FinancialAdvisor.generateResponse(userInput, {
      income: userProfile?.financialSnapshot.monthlyIncome,
      expenses: userProfile?.financialSnapshot.monthlyExpenses,
      goals: userProfile?.preferences.primaryGoals,
      riskTolerance: userProfile?.preferences.riskTolerance
    });

    const category = determineCategory(userInput);
    
    // Get personalized advice based on habits
    if (userProfile) {
      const habitAnalysis = UserProfileManager.analyzeHabits(userProfile.habits);
      response.tips = [
        ...response.tips,
        ...habitAnalysis.recommendations
      ];
    }
    
    // Add personality-specific response styling
    const formattedResponse = getPersonalityResponse(
      personality,
      formatAIResponse(response),
      category,
      userProfile
    );
    
    return {
      id: Date.now().toString(),
      type: 'assistant',
      content: formattedResponse,
      category,
      timestamp: new Date()
    };
  };

  // Determine message category based on content
  const determineCategory = (input: string): 'financial' | 'sustainability' | 'alert' | 'success' => {
    if (input.toLowerCase().includes('emergency') || input.toLowerCase().includes('alert')) {
      return 'alert';
    }
    if (input.toLowerCase().includes('sustain') || input.toLowerCase().includes('green')) {
      return 'sustainability';
    }
    if (input.toLowerCase().includes('success') || input.toLowerCase().includes('achieve')) {
      return 'success';
    }
    return 'financial';
  };

  // Enhanced message handling
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    addMessage(userMessage);
    announceMessage(userMessage);
    setInput('');
    setIsTyping(true);
    scrollToBottom(false);

    // Analyze user context from message
    if (input.toLowerCase().includes('income')) {
      const incomeMatch = input.match(/\$?\d+(?:,\d{3})*(?:\.\d{2})?/);
      if (incomeMatch) {
        updateUserProfile({
          financialSnapshot: {
            ...userProfile?.financialSnapshot,
            monthlyIncome: parseFloat(incomeMatch[0].replace(/[$,]/g, ''))
          }
        });
      }
    }

    // Generate AI response with personalization
    setTimeout(() => {
      const aiMessage = generateResponse(input.trim().toLowerCase());
      setMessages(prev => [...prev, aiMessage]);
      addMessage(aiMessage);
      announceMessage(aiMessage);
      setIsTyping(false);
      scrollToBottom();
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'financial':
        return <PiggyBank className="w-5 h-5 text-blue-500" />;
      case 'sustainability':
        return <Leaf className="w-5 h-5 text-green-500" />;
      case 'alert':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      default:
        return <Sparkles className="w-5 h-5 text-purple-500" />;
    }
  };

  // Update typing indicator
  const TypingIndicator = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex justify-start"
      aria-label="Assistant is typing"
    >
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-[#68A511]" aria-hidden="true" />
          <span className="text-sm text-gray-500">
            {personality.messageStyle.thinking}
          </span>
        </div>
      </div>
    </motion.div>
  );

  // Add personality change handler with profile update
  const handlePersonalityChange = (newPersonality: Personality) => {
    setPersonality(newPersonality);
    addMessage({
      id: Date.now().toString(),
      type: 'assistant',
      content: newPersonality.messageStyle.greeting,
      category: 'success',
      timestamp: new Date()
    });
    setShowPersonalitySelector(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8" role="complementary" aria-label="AI Assistant Chat">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] sm:w-[380px] max-w-full mb-4 border border-gray-200 dark:border-gray-700"
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-title"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <Bot className="w-6 h-6 text-[#68A511]" aria-hidden="true" />
                <div>
                  <h3 id="chat-title" className="font-semibold text-gray-800 dark:text-white">
                    Pocket {personality.emoji}
                  </h3>
                  <button
                    onClick={() => setShowPersonalitySelector(!showPersonalitySelector)}
                    className="text-xs text-gray-500 hover:text-[#68A511] transition-colors"
                  >
                    {personality.name} • Change Personality
                  </button>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#68A511]"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {showPersonalitySelector && (
              <PersonalitySelector
                onSelect={handlePersonalityChange}
                currentPersonality={personality}
              />
            )}

            <div 
              className="h-[50vh] sm:h-[400px] overflow-y-auto p-4 space-y-4"
              role="log"
              aria-live="polite"
              aria-label="Chat messages"
            >
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] sm:max-w-[80%] rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-[#68A511] text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white'
                    }`}
                    role="article"
                    aria-label={`${message.type === 'user' ? 'Your message' : 'Assistant response'}`}
                  >
                    {message.type === 'assistant' && (
                      <div className="flex items-center space-x-2 mb-1">
                        {getCategoryIcon(message.category)}
                        <span className="text-xs font-medium">
                          {message.category?.charAt(0).toUpperCase() + message.category?.slice(1)}
                        </span>
                      </div>
                    )}
                    <p className="text-sm break-words">{message.content}</p>
                  </div>
                </motion.div>
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} tabIndex={-1} />
            </div>

            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(); }} 
              className="p-4 border-t border-gray-200 dark:border-gray-700"
              aria-label="Chat input form"
            >
              <div className="flex items-center space-x-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about your finances..."
                  className="flex-1 min-h-[40px] max-h-[120px] p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#68A511] dark:bg-gray-700 dark:text-white resize-none"
                  rows={1}
                  aria-label="Chat message input"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="p-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#68A511]"
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && messages.length === 0) {
            setShowPersonalitySelector(true);
          }
        }}
        className="bg-[#68A511] text-white p-4 rounded-full shadow-lg hover:bg-[#527f0e] transition-colors"
        aria-label={isOpen ? "Close Pocket" : "Open Pocket"}
      >
        {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Bot className="w-6 h-6" aria-hidden="true" />}
      </motion.button>
    </div>
  );
};

export default AIAssistant; 